/**
 * Prominent inline approval action block for tool calls requiring human approval.
 *
 * Rendered OUTSIDE the collapsible "Used N tools" section so users can't miss it.
 * Shows approve/deny buttons when pending, compact resolved chip when done.
 *
 * Single source of truth for action behavior + tooltips: `useApprovalActions`.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldCheck, ShieldX, ChevronRight, Check, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { getModuleById } from '../../config/modules.config';
import { useApproval, useApprovalActions, useModuleName } from '../../hooks';
import { isToolCallOutputError, type ToolCallData } from './types';

interface ApprovalActionBlockProps {
  toolCall: ToolCallData;
  onOpenDetail?: (toolCallId: string) => void;
  /** Optional: collapse the inline pending banner immediately on resolve start
   *  so the user doesn't see it flash back while the backend executes. */
  onResolveStart?: (toolCallId: string, action: 'approve' | 'reject' | 'always_approve') => void;
}

/** Format tool name for display (strip module prefix and _api_v1_ suffix) */
function formatToolName(toolName: string): string {
  const rawName = toolName.includes('__')
    ? toolName.split('__').slice(1).join('__')
    : toolName;
  const apiIdx = rawName.indexOf('_api_v1_');
  const cleanName = apiIdx > 0 ? rawName.substring(0, apiIdx) : rawName;
  return cleanName.replace(/_/g, ' ');
}

export const ApprovalActionBlock: React.FC<ApprovalActionBlockProps> = ({
  toolCall,
  onOpenDetail,
  onResolveStart,
}) => {
  const { t } = useTranslation(['aiChat', 'common']);
  const getModuleName = useModuleName();
  const isPending = toolCall.status === 'pending_approval' && !!toolCall.approval_id;

  // Only fetch the approval when we actually need to render action buttons
  const { data: approval } = useApproval(isPending ? toolCall.approval_id : undefined);
  const { resolve, resolving, tooltips, alwaysApproveNode } = useApprovalActions(approval, {
    onResolveStart: (action) => {
      if (action !== 'edit') onResolveStart?.(toolCall.id, action);
    },
  });

  const displayName = formatToolName(toolCall.tool_name);
  const moduleConfig = getModuleById(toolCall.module);
  const moduleLabel = moduleConfig ? getModuleName(moduleConfig) : toolCall.module.replace(/_/g, ' ');

  // Executing state — approved but still running (waiting for backend result)
  const isExecuting = toolCall.status === 'running' && !!toolCall.approval_id;
  if (isExecuting) {
    return (
      <div className={cn(
        'inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs',
        'border-info/40 bg-info/10 text-info',
      )}>
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
        <span className="font-medium">{displayName}</span>
        <span className="text-[10px] opacity-70">{t('aiChat:toolExecuting', 'Executing...')}</span>
      </div>
    );
  }

  // Resolved state — compact chip. Three outcomes:
  //   approved : tool executed cleanly → green
  //   denied   : user rejected the approval → red
  //   failed   : tool executed but returned an error (API error, validation, etc.) → amber
  if (!isPending) {
    const isDenied = toolCall.status === 'error'
      && typeof toolCall.error_message === 'string'
      && toolCall.error_message.toLowerCase().includes('reject');
    const hasExecutionError = toolCall.status === 'error' || isToolCallOutputError(toolCall);
    const isApproved = toolCall.status === 'success' && !hasExecutionError;
    const variantClasses = isApproved
      ? 'border-success/30 bg-success/10 text-success'
      : isDenied
        ? 'border-destructive/30 bg-destructive/10 text-destructive'
        : 'border-warning/30 bg-warning/10 text-warning';
    const label = isApproved
      ? t('aiChat:toolApproved')
      : isDenied
        ? t('aiChat:toolDenied')
        : t('aiChat:toolFailed', 'Failed');
    return (
      <div
        className={cn(
          'inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs',
          'cursor-pointer hover:opacity-80 transition-opacity',
          variantClasses,
        )}
        onClick={() => toolCall.id && onOpenDetail?.(toolCall.id)}
        title={`${moduleLabel}: ${displayName}`}
      >
        {isApproved ? (
          <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <ShieldX className="h-3.5 w-3.5 shrink-0" />
        )}
        <span className="font-medium">{displayName}</span>
        <span className="text-[10px] opacity-70">{label}</span>
        <ChevronRight className="h-3 w-3 opacity-40 shrink-0 rtl:rotate-180" />
      </div>
    );
  }

  // Pending state — prominent action block
  const compactBtn = 'h-7 px-2 text-xs gap-1';
  const iconCls = 'h-3 w-3';
  const isLoading = resolving !== null;
  return (
    <div className={cn(
      'flex items-center gap-2 px-2.5 py-1.5 rounded-lg border w-full',
      'border-warning/40 bg-warning/10',
    )}>
      <ShieldCheck className="h-4 w-4 text-warning shrink-0" />
      <div className="flex items-baseline gap-1.5 min-w-0 flex-1 overflow-hidden">
        <span className="text-xs font-medium text-warning truncate">
          {displayName}
        </span>
        <span className="text-[10px] text-warning/70 truncate hidden sm:inline">
          {moduleLabel}
        </span>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <Button
          size="sm"
          variant="outline"
          title={tooltips.reject}
          className={cn(compactBtn, 'text-destructive hover:bg-destructive hover:text-destructive-foreground')}
          onClick={() => resolve('reject')}
          disabled={isLoading}
        >
          {resolving === 'reject' ? <Loader2 className={cn(iconCls, 'animate-spin')} /> : <X className={iconCls} />}
          {t('common:app.reject')}
        </Button>
        <Button
          size="sm"
          variant="default"
          title={tooltips.approve}
          className={cn(compactBtn, 'bg-success hover:bg-success/90 text-success-foreground')}
          onClick={() => resolve('approve')}
          disabled={isLoading}
        >
          {resolving === 'approve' ? <Loader2 className={cn(iconCls, 'animate-spin')} /> : <Check className={iconCls} />}
          {t('common:app.approve')}
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant="default"
              className={cn(compactBtn, 'bg-info hover:bg-info/90 text-info-foreground')}
              onClick={() => resolve('always_approve')}
              disabled={isLoading}
            >
              {resolving === 'always_approve' ? <Loader2 className={cn(iconCls, 'animate-spin')} /> : <Check className={iconCls} />}
              {t('common:app.alwaysApprove')}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{alwaysApproveNode}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
